// マスター編集のランディング画面。2段タブ(系統 → 個別マスタ)で全マスタを管理する。
// rev: WorkClassification / MaterialMaster / MixRatio は専用 UI へディスパッチする。
// CSV の出し入れはマスタ横断のため、表示中のマスタに関わらず常に画面上部から開けるようにしている。
import { Box, Stack, Typography, Chip, Paper, Tabs, Tab, Alert, Divider, Button, Snackbar, Tooltip } from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import { allMasterConfigs } from "./configs";
import MasterCrudPage from "./MasterCrudPage";
import WorkClassificationPage from "./WorkClassificationPage";
import MaterialMasterPage from "./MaterialMasterPage";
import MixRatioMasterPage from "./MixRatioMasterPage";
import WorkMasterPage from "./WorkMasterPage";
import MasterCsvExportDialog from "./MasterCsvExportDialog";
import MasterCsvImportDialog from "./MasterCsvImportDialog";
import { activeRowCountOf } from "./masterRowCounts";
import { useFullDb, syncMastersFromServer, loadDbSlice, ALL_SLICE_KEYS } from "../../../api/dbSlice";
import type { MasterCsvExportSource } from "./csv/masterCsvExport";
import { useEffect, useState } from "react";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import { objectKeys } from "../../../util/typed";
import { useIsOnline } from "../../../util/useIsOnline";
import { OfflineMasterBanner } from "../../../components/OfflineMasterBanner";
import { ContentLoading } from "../../../components/Loading";

// 旧キー → 新専用 UI キーへのリダイレクト表 (URL 互換維持)。
const LEGACY_REDIRECT: Record<string, string> = {
  "work-type": "work-classification",
  "work-subtype": "work-classification",
  "work-detail": "work-classification",
  "material": "material-master",
  "material-detail": "material-master",
  "mix-ratio": "mix-ratio-master",
  "mix-ratio-detail": "mix-ratio-master",
};

type SystemTier = "division" | "work" | "material";

const systemLabel: Record<SystemTier, string> = {
  division: "マスタ区分",
  material: "材料マスタ",
  work: "工事マスタ",
};

// 各マスタの系統 + 説明
const masterMeta: Record<string, { system: SystemTier; desc: string }> = {
  "area-division": { system: "division", desc: "案件・現場単位の区分。" },
  "material-division": { system: "division", desc: "材料の体系区分。現在は「共通材料」のみを使います。" },
  "client": { system: "division", desc: "発注者組織(首都高速道路 等)。" },
  "work-master": { system: "work", desc: "個別の工事(整理番号・発注者・工期・塗装種類 等)と場所区分ラベル設定 / アクセススコープ。" },
  "work-classification": { system: "work", desc: "工種 / 種別 / 工程の 3 層ネスト編集。" },
  "carry-in-location": { system: "work", desc: "搬入場所(川崎現場倉庫 等)。" },
  "location-division": { system: "work", desc: "場所区分(区分1〜10)。" },
  "quality-measurement-item": { system: "work", desc: "品質測定項目(含水率・膜厚 等)。" },
  "material-master": { system: "material", desc: "材料 + 細目のネスト編集 (アコーディオン)。" },
  "stirring-method": { system: "material", desc: "ハンドミキサー／ミキサー／手練り 等。" },
  "construction-method": { system: "material", desc: "左官／シール／吹付／注入／塗布 等。" },
  "pot-life": { system: "material", desc: "材料 × 気温帯別 の可使時間定義。" },
  "mix-ratio-master": { system: "material", desc: "配合比 + 明細のネスト編集 (アコーディオン)。" },
};

// 系統ごとの並び順(タブ表示順)
const MASTER_ORDER_BY_SYSTEM: Record<SystemTier, string[]> = {
  division: ["area-division", "material-division", "client"],
  work: ["work-master", "work-classification", "carry-in-location", "location-division", "quality-measurement-item"],
  material: ["material-master", "stirring-method", "construction-method", "pot-life", "mix-ratio-master"],
};

// オフライン中にCSVの出し入れを押せない理由。マスタ編集はオンライン限定で、
// 書き出しも端末に残った控えではなくサーバーの最新から作るため、どちらも復帰を待ってもらう。
const OFFLINE_CSV_MESSAGE = "現在オフラインです。CSVの書き出しと取り込みは、オンラインに復帰してから行えます。";

const OFFLINE_EXPORT_MESSAGE = "オフラインのため、サーバーから最新のマスタを取り直せませんでした。";

// 押せない理由の吹き出し。押せる状態では何も出さない。
function offlineNoticeOf(isOnline: boolean): string {
  if (isOnline) {
    return "";
  }
  return OFFLINE_CSV_MESSAGE;
}

const systemOfMaster = (key: string): SystemTier => masterMeta[key]?.system ?? "division";

const firstMasterInSystem = (s: SystemTier): string => MASTER_ORDER_BY_SYSTEM[s]?.[0] ?? "area-division";

// 表示名は専用キー用にハードコーディング (allMasterConfigs に居ないため)。
const DEDICATED_TITLE: Record<string, string> = {
  "work-master": "工事マスタ",
  "work-classification": "工種 / 種別 / 工程",
  "material-master": "材料マスタ (細目含む)",
  "mix-ratio-master": "配合比マスタ (明細含む)",
};
function titleOf(key: string): string {
  const dedicated = DEDICATED_TITLE[key];
  if (dedicated !== undefined) {
    return dedicated;
  }
  return allMasterConfigs.find((m) => m.key === key)?.config.title ?? key;
}

export default function MasterListPage({ pageContext }: { pageContext: PageContext }) {
  const navigate = useNavigate();
  const location = useLocation();
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const { db, loading } = useFullDb(idb, ownerId);
  const isOnline = useIsOnline(pageContext.api);

  // URL 末尾セグメントを取得 (`/console/masters/{master-key}`)
  const rawSeg = (() => {
    const path = location.pathname.replace(/\/$/, "");
    return path.split("/").pop() ?? "";
  })();

  // 旧キーは新専用 UI キーへ変換。Navigate replace は後段で行う (Rules of Hooks 対応)。
  const redirectTo = LEGACY_REDIRECT[rawSeg] ?? null;
  const urlMasterKey = masterMeta[rawSeg] ? rawSeg : undefined;

  const [masterKey, setMasterKey] = useState<string>(urlMasterKey ?? firstMasterInSystem("division"));

  // URL 追従
  useEffect(() => {
    const isChangedByUrl = urlMasterKey !== undefined && urlMasterKey !== masterKey;
    if (isChangedByUrl) {
      setMasterKey(urlMasterKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMasterKey]);

  useEffect(() => {
    // 旧キーアクセス時 (redirectTo あり) は <Navigate> 側に任せ、ここで上書きしない。
    const needsUrlFix = urlMasterKey === undefined && redirectTo === null;
    if (needsUrlFix) {
      navigate(`/console/masters/${masterKey}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSystem: SystemTier = systemOfMaster(masterKey);
  const mastersToShow = MASTER_ORDER_BY_SYSTEM[currentSystem];

  const onSystemChange = (sys: SystemTier) => {
    const first = firstMasterInSystem(sys);
    setMasterKey(first);
    navigate(`/console/masters/${first}`);
  };

  const onMasterChange = (key: string) => {
    setMasterKey(key);
    navigate(`/console/masters/${key}`);
  };

  // CSV の出し入れポップアップの開閉と、取り込み完了の知らせ。
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);
  const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
  const [importedMessage, setImportedMessage] = useState<string>("");

  // 取り込みが確定したらサーバーからマスタを取り直し、ローカルの表示を最新に揃える。
  const onImported = async (message: string) => {
    await syncMastersFromServer(pageContext.api, idb, ownerId);
    setImportedMessage(message);
  };

  // 書き出しの直前にサーバーからマスタを取り直し、読み直した最新の内容を渡す。
  // オフラインだと取り直しが行われないまま素通りしてしまうため、その場合は書き出さずに知らせる。
  const refreshMastersForExport = async (): Promise<MasterCsvExportSource> => {
    if (!pageContext.api.isOnline) {
      throw new Error(OFFLINE_EXPORT_MESSAGE);
    }
    await syncMastersFromServer(pageContext.api, idb, ownerId);
    return await loadDbSlice(idb, ownerId, ALL_SLICE_KEYS);
  };

  // 旧 URL アクセスは新キーへリダイレクト (hooks 後の安全な位置で実施)。
  if (redirectTo) {
    return <Navigate replace to={`/console/masters/${redirectTo}`} />;
  }

  if (loading) {
    return <ContentLoading />;
  }

  const offlineNotice = offlineNoticeOf(isOnline);
  const currentCount = activeRowCountOf(db, masterKey);
  const currentTitle = titleOf(masterKey);
  const currentDesc = masterMeta[masterKey]?.desc ?? "";

  return (
    <Stack p={3} maxWidth={1400} mx="auto" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <StorageIcon color="action" />
        <Typography variant="h4" fontWeight="bold">マスター編集</Typography>
        <Chip size="small" color="info" label={`${Object.keys(masterMeta).length} マスタ`} sx={{ pointerEvents: "none" }} />
        <Box flex={1} />
        {/* 押せないボタンはマウスの操作を受け取らないため、理由の吹き出しは外側の要素で受ける。 */}
        <Tooltip title={offlineNotice}>
          <span>
            <Button startIcon={<DownloadIcon />} variant="outlined" size="small" disabled={!isOnline} onClick={() => setIsExportOpen(true)}>CSV エクスポート</Button>
          </span>
        </Tooltip>
        <Tooltip title={offlineNotice}>
          <span>
            <Button startIcon={<UploadIcon />} variant="outlined" size="small" disabled={!isOnline} onClick={() => setIsImportOpen(true)}>CSV インポート</Button>
          </span>
        </Tooltip>
      </Stack>

      <MasterCsvExportDialog
        open={isExportOpen}
        db={db}
        isOnline={isOnline}
        refreshMasters={refreshMastersForExport}
        onClose={() => setIsExportOpen(false)}
        showError={pageContext.api.showError}
      />
      <MasterCsvImportDialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        importMasters={(request) => pageContext.api.importMasters(request)}
        onImported={onImported}
      />
      <Snackbar
        open={importedMessage !== ""}
        autoHideDuration={6000}
        onClose={() => setImportedMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        message={importedMessage}
      />

      <OfflineMasterBanner isOnline={isOnline} />

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.masterList.info)}</Alert>

      <Paper variant="outlined">
        <Tabs
          value={currentSystem}
          onChange={(_, v) => {
            if (v === "division" || v === "work" || v === "material") {
              onSystemChange(v);
            }
          }}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": { fontSize: 15, fontWeight: 600, py: 1.25 },
          }}
        >
          {objectKeys(systemLabel).map((s) => (
            <Tab
              key={s}
              value={s}
              label={
                <Stack direction="row" alignItems="center" gap={1} justifyContent="center">
                  <Typography variant="body2" fontWeight={600}>{systemLabel[s]}</Typography>
                  {/* クリックは親 Tab に透過させる(MUI 7.1.1 の Chip は onClick 未指定でもクリック時に呼び出して TypeError になるため、装飾 Chip はクリックを受けない)。 */}
                  <Chip size="small" variant="outlined" label={`${MASTER_ORDER_BY_SYSTEM[s].length}マスタ`} sx={{ pointerEvents: "none" }} />
                </Stack>
              }
            />
          ))}
        </Tabs>

        <Tabs
          value={masterKey}
          onChange={(_, v) => onMasterChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, fontSize: 13, py: 1 } }}
        >
          {mastersToShow.map((k) => (
            <Tab key={k} value={k} label={`${titleOf(k)} (${activeRowCountOf(db, k)})`} />
          ))}
        </Tabs>
      </Paper>

      <Paper variant="outlined" sx={{ px: 2, py: 1.5, backgroundColor: "grey.50" }}>
        <Stack direction="row" alignItems="baseline" gap={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">{currentTitle}</Typography>
          <Chip size="small" label={`${currentCount}件`} sx={{ pointerEvents: "none" }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5}>{currentDesc}</Typography>
      </Paper>

      <Divider />

      <MasterContent masterKey={masterKey} pageContext={pageContext} />
    </Stack>
  );
}

// 選択中のマスタに対応する編集画面を出す。専用 UI を持つマスタはそちらへ、それ以外は汎用 CRUD へ渡す。
function MasterContent({ masterKey, pageContext }: { masterKey: string; pageContext: PageContext }) {
  if (masterKey === "work-master") {
    return <WorkMasterPage pageContext={pageContext} showOfflineBanner={false} />;
  }
  if (masterKey === "work-classification") {
    return <WorkClassificationPage pageContext={pageContext} showOfflineBanner={false} />;
  }
  if (masterKey === "material-master") {
    return <MaterialMasterPage pageContext={pageContext} showOfflineBanner={false} />;
  }
  if (masterKey === "mix-ratio-master") {
    return <MixRatioMasterPage pageContext={pageContext} showOfflineBanner={false} />;
  }
  const config = allMasterConfigs.find((entry) => entry.key === masterKey)?.config;
  if (config !== undefined) {
    return <MasterCrudPage key={masterKey} config={config} pageContext={pageContext} />;
  }
  // 上のタブに無いマスタ名が URL で指定された場合など、対応する編集画面が引けなかったときの表示。
  return (
    <Box p={4}>
      <Typography color="text.secondary">
        選択されたマスタに対応する編集画面が見つかりませんでした。上のタブからマスタを選び直してください。
      </Typography>
    </Box>
  );
}
